import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useAddFileTranscriptMutation,
  useAddManualTranscriptMutation,
  useEvaluateFileMutation,
  useGetCourseCategoriesQuery,
} from '@teachur/http-client';
import type { AddManualTranscriptInput } from '@teachur/http-client';

import { getErrorMessage } from '@new/lib/getErrorMessage';
import { useUploadFile } from '@new/features/transcripts/shared/hooks/useUploadFile';
import {
  canSaveReview,
  emptyReview,
  isTransferableGrade,
  makeKeyGen,
  normalizeGrade,
  sumCreditHours,
  toCoursePayload,
  useReviewEdits,
  type ReviewCourse,
  type ReviewState,
} from './reviewState';

export type AddTranscriptMethod = 'file' | 'manual';
export type AddTranscriptStep = 'method' | 'upload' | 'generating' | 'review' | 'success';

export type { ReviewCourse, ReviewState, ReviewController } from './reviewState';

const nextKey = makeKeyGen('c');

export const useAddTranscript = (options?: {
  onDone?: () => void;
  initialMethod?: AddTranscriptMethod;
}) => {
  const initialMethod = options?.initialMethod ?? 'manual';
  const startStep: AddTranscriptStep = initialMethod === 'file' ? 'upload' : 'review';

  const [step, setStep] = useState<AddTranscriptStep>(startStep);
  const [method, setMethod] = useState<AddTranscriptMethod>(initialMethod);
  const [review, setReview] = useState<ReviewState>(emptyReview());
  const [aiError, setAiError] = useState<string | null>(null);

  const upload = useUploadFile();
  const evaluateFile = useEvaluateFileMutation();
  const addFile = useAddFileTranscriptMutation();
  const addManual = useAddManualTranscriptMutation();
  const categoriesQuery = useGetCourseCategoriesQuery();
  const categories = categoriesQuery.data ?? [];

  const reset = useCallback(() => {
    setStep(startStep);
    setMethod(initialMethod);
    setReview(emptyReview());
    setAiError(null);
  }, [initialMethod, startStep]);

  const submitFile = useCallback(
    async (
      file: File,
      details: {
        institution: string;
        institutionUrl?: string;
      }
    ) => {
      const { institution } = details;
      const baseReview: ReviewState = {
        ...emptyReview(),
        institution,
        institutionUrl: details.institutionUrl ?? '',
        officialTranscriptRequested: false,
      };
      setAiError(null);
      const fileId = await upload.uploadFile(file);
      if (fileId == null) {
        const message = upload.error ?? 'File upload failed';
        setAiError(message);
        toast.error(message);
        setReview(baseReview);
        setStep('review');
        return;
      }

      setStep('generating');
      const formData = new FormData();
      formData.append('file', file);

      try {
        const extracted = await evaluateFile.mutateAsync(formData);
        const courses: ReviewCourse[] = (extracted.courses ?? [])
          .map((c) => ({
            key: nextKey(),
            rawName: c.rawName,
            rawCode: c.rawCode ?? null,
            // Credits are always whole numbers on the student side — round the
            // AI-extracted value (e.g. 1.5 -> 2) so no fractional credit ever
            // enters the review state.
            creditHours: c.creditHours == null ? null : Math.round(Number(c.creditHours)),
            grade: normalizeGrade(c.grade ?? null),
            // API resolves the AI category code to real ids; use them directly.
            categoryIds: c.categoryIds ?? [],
          }))
          .filter((c) => isTransferableGrade(c.grade));
        setReview({
          ...baseReview,
          institution: institution || extracted.institution || '',
          accreditingOrganizations: extracted.accreditingOrganizations ?? [],
          awardedDegree: extracted.awardedDegree ?? null,
          unofficialCredits: sumCreditHours(courses),
          courses,
          unofficialFileId: fileId,
        });
      } catch (err) {
        const message = getErrorMessage(err, 'Transcript extraction failed');
        setAiError(message);
        toast.error(message);
        setReview({ ...baseReview, unofficialFileId: fileId });
      } finally {
        setStep('review');
      }
    },
    [evaluateFile, upload]
  );

  const edits = useReviewEdits(setReview, nextKey);

  const save = useCallback(async () => {
    if (!canSaveReview(review)) return;
    const base: AddManualTranscriptInput = {
      institution: review.institution,
      institutionUrl: review.institutionUrl.trim() || null,
      accreditingOrganizations: review.accreditingOrganizations,
      awardedDegree: review.awardedDegree,
      officialTranscriptRequested: review.officialTranscriptRequested,
      unofficialCredits: Number(review.unofficialCredits) || 0,
      courses: toCoursePayload(review.courses),
    };
    try {
      if (review.unofficialFileId != null) {
        await addFile.mutateAsync({ ...base, unofficialFileId: review.unofficialFileId });
      } else {
        await addManual.mutateAsync(base);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save transcript'));
      return;
    }
    setStep('success');
    options?.onDone?.();
  }, [addFile, addManual, options, review]);

  const canSave = useMemo(() => canSaveReview(review), [review]);

  return {
    step,
    method,
    review,
    aiError,
    categories,
    isUploading: upload.isLoading,
    isSaving: addFile.isPending || addManual.isPending,
    canSave,
    canGoBack: method === 'file' && step === 'review',
    goBack: () => {
      if (method === 'file') setStep('upload');
    },
    reset,
    submitFile,
    ...edits,
    save,
  };
};
